import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Tag, Loader2 } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { articleSchema } from "@/utils/schemas";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching blog post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const blogSchemas = useMemo(() => {
    if (!post) return [];
    return [articleSchema({
      title: post.title,
      description: post.meta_description,
      image: post.image_url,
      date: post.created_at,
      modified: post.updated_at,
      url: `https://overhaulops.com/blog/${post.slug}`,
    })];
  }, [post]);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#b51a00]" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title={post.title}
        description={post.meta_description}
        keywords={post.keywords?.join(", ")}
        image={post.image_url}
        schemas={blogSchemas}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }, { name: post.title }]}
      />
      <div data-testid="blog-post-page" className="pt-20">
        {/* Hero */}
        <section className="py-16 bg-[#1a1a1a] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="inline-flex items-center text-gray-400 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
            <h1 
              className="text-4xl md:text-5xl font-bold leading-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {post.title}
            </h1>
            <div className="flex items-center gap-4 mt-6 text-gray-400">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Recently'}</span>
              </div>
              <span>•</span>
              <span>{post.author}</span>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        {post.image_url && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
            <img 
              src={post.image_url} 
              alt={post.title}
              className="w-full aspect-video object-cover rounded-sm shadow-lg"
            />
          </div>
        )}

        {/* Content */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <article 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Keywords/Tags */}
            {post.keywords?.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-4 h-4 text-gray-400" />
                  {post.keywords.map((keyword, i) => (
                    <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 p-8 bg-[#f4f4f5] rounded-sm">
              <h3 
                className="text-2xl font-bold mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Need Junk Removal in DFW?
              </h3>
              <p className="text-gray-600 mb-6">
                Overhaul Ops is your firefighter-owned, fully insured junk removal company serving Denton, Tarrant, Parker, and Wise counties.
              </p>
              <Link to="/contact">
                <Button className="bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm px-6">
                  Get a Free Quote
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
